import React from "react";
import { motion } from "framer-motion";
import * as Dialog from "@radix-ui/react-dialog";
import { recalculateAllStats } from "../utils/recalculateStats";

/**
 * Radix dialog for game settings — data export/import, theme selection (5 eras),
 * and feature toggles (scanlines, static background, auto-expand collections, migration banner).
 *
 * @param {{ onClose: () => void, userTheme: string, currentTheme: string, setCurrentTheme: (t: string) => void, scanlinesEnabled: boolean, setScanlinesEnabled: (v: boolean) => void, staticBgEnabled: boolean, setStaticBgEnabled: (v: boolean) => void, collectionExpandDefault: boolean, setCollectionExpandDefault: (v: boolean) => void, showMigrationBanner: boolean, setShowMigrationBanner: (v: boolean) => void }} props
 * @param {() => void} props.onClose - Callback to dismiss the dialog.
 * @param {string} props.userTheme - User's preferred theme ID.
 * @param {string} props.currentTheme - Currently active theme.
 * @param {(t: string) => void} props.setCurrentTheme - Theme setter.
 * @param {boolean} props.scanlinesEnabled - Whether HUD scanlines overlay is visible.
 * @param {(v: boolean) => void} props.setScanlinesEnabled - Scanlines toggle setter.
 * @param {boolean} props.staticBgEnabled - Whether static colors replace rotating wallpapers.
 * @param {(v: boolean) => void} props.setStaticBgEnabled - Static bg toggle setter.
 * @param {boolean} props.collectionExpandDefault - Whether collection sections auto-expand.
 * @param {(v: boolean) => void} props.setCollectionExpandDefault - Auto-expand setter.
 * @param {boolean} props.showMigrationBanner - Whether the domain-migration banner is shown.
 * @param {(v: boolean) => void} props.setShowMigrationBanner - Migration banner setter.
 * @returns {JSX.Element} The settings modal.
 */
function SettingsModal({ onClose, userTheme, currentTheme, setCurrentTheme, scanlinesEnabled, setScanlinesEnabled, staticBgEnabled, setStaticBgEnabled, collectionExpandDefault, setCollectionExpandDefault, showMigrationBanner, setShowMigrationBanner }) {

  /** Serializes all kisekidle-* localStorage entries into a downloadable .json file. */
  const handleExport = () => {
    const prefixes = ["kisekidle-", "synced-", "collapsed_", "voted_"];
    const saveData = {};

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && prefixes.some((p) => key.startsWith(p))) {
        saveData[key] = localStorage.getItem(key);
      }
    }

    const blob = new Blob([JSON.stringify(saveData)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "kisekidle_backup.json";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  /** Reads a previously exported backup .json file and restores all localStorage entries. */
  const handleImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target.result);

        const isNewFormat = Object.keys(data).some(
          (k) => k.startsWith("kisekidle-") || k.startsWith("synced-")
        );

        if (isNewFormat) {
          for (const [key, value] of Object.entries(data)) {
            localStorage.setItem(key, value);
          }
        } else {
          const legacyMap = {
            chars: "kisekidle-chars",
            quotes: "kisekidle-quotes",
            music: "kisekidle-music",
            locations: "kisekidle-locations",
            trivia: "kisekidle-trivia",
            crafts: "kisekidle-crafts",
            silhouette: "kisekidle-silhouette",
            collection: "kisekidle-collection",
            stats: "kisekidle-stats",
          };
          for (const [oldKey, value] of Object.entries(data)) {
            if (legacyMap[oldKey] && value) {
              localStorage.setItem(legacyMap[oldKey], value);
            }
          }
        }

        alert("Data imported successfully! The page will now reload.");
        window.location.reload();
      } catch (err) {
        alert(
          "Failed to import. Please make sure this is a valid Kisekidle backup file."
        );
      }
    };
    reader.readAsText(file);
  };

  return (
    <Dialog.Root open={true} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay forceMount asChild>
          <motion.div
            className={`modal-overlay theme-${currentTheme}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
        </Dialog.Overlay>
        <div className={`modal-content-wrapper theme-${currentTheme}`}>
          <Dialog.Content forceMount asChild>
            <motion.div
              className="modal-content settings-modal-content"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
            >
              <Dialog.Title className="sr-only">Settings</Dialog.Title>
              <Dialog.Description className="sr-only">
                Manage your data and choose your UI theme.
              </Dialog.Description>

              <Dialog.Close asChild>
                <button className="modal-close">
                  ✖
                </button>
              </Dialog.Close>

              <h2 className="mode-title" style={{ marginBottom: "20px" }}>⚙️ Settings</h2>

              <div className="settings-section">
                <p style={{ fontSize: "14px", marginBottom: "20px", opacity: 0.8 }}>
                  Transfer your stats and collection to another browser or device.
                </p>
                <div className="settings-button-group">
                  <button className="import-export-btn premium-hover" onClick={handleExport}>
                    📥 Download Backup (.json)
                  </button>
                  <label
                    className="import-export-btn premium-hover"
                    style={{ display: "inline-block", cursor: "pointer", textAlign: "center" }}
                  >
                    📤 Import Backup
                    <input
                      type="file"
                      accept=".json"
                      style={{ display: "none" }}
                      onChange={handleImport}
                    />
                  </label>
                  <button
                    className="import-export-btn premium-hover"
                    onClick={() => { recalculateAllStats(); alert("Statistics have been recalculated from your play history! The page will now reload."); window.location.reload(); }}
                    style={{ backgroundColor: "rgba(74, 85, 104, 0.5)" }}
                  >
                    🔄 Recalculate Stats
                  </button>
                </div>
              </div>

              <div className="settings-section" style={{ marginTop: "30px", borderTop: "1px solid var(--era-border)", paddingTop: "20px" }}>
                <h3 className="mode-title" style={{ fontSize: "16px", marginBottom: "15px" }}>✨ Choose Orbal Era (UI Theme)</h3>
                <div className="theme-selection-grid">
                  {[
                    { id: "legacy", label: "📜 Legacy", color: "var(--era-accent-legacy)" },
                    { id: "sky", label: "🕰️ Sky", color: "var(--era-accent-sky)" },
                    { id: "coldsteel", label: "⚔️ Cold Steel", color: "var(--era-accent-coldsteel)" },
                    { id: "daybreak", label: "🛰️ Daybreak", color: "var(--era-accent-daybreak)" },
                    { id: "random", label: "✨ Random", color: "var(--era-accent)" },
                  ].map((t) => (
                    <motion.button
                      key={t.id}
                      whileHover={{ scale: 1.05, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      className={`theme-btn ${userTheme === t.id ? "active" : ""}`}
                      onClick={() => setCurrentTheme(t.id)}
                      style={{
                        position: "relative",
                        overflow: "hidden"
                      }}
                    >
                      {t.label}
                      {userTheme === t.id && (
                        <motion.div
                          layoutId="active-theme-glow"
                          className="active-theme-glow"
                        />
                      )}
                    </motion.button>
                  ))}
                </div>
              </div>

              <div className="settings-section" style={{ marginTop: "20px", display: "flex", flexDirection: "column", gap: "12px" }}>
                <label className="premium-toggle">
                  <input
                    type="checkbox"
                    checked={scanlinesEnabled}
                    onChange={(e) => setScanlinesEnabled(e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-label">Show HUD scanlines (Xipha / ARCUS)</span>
                </label>

                <label className="premium-toggle">
                  <input
                    type="checkbox"
                    checked={staticBgEnabled}
                    onChange={(e) => setStaticBgEnabled(e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-label">Static background (theme colors instead of wallpapers)</span>
                </label>

                <label className="premium-toggle">
                  <input
                    type="checkbox"
                    checked={collectionExpandDefault}
                    onChange={(e) => setCollectionExpandDefault(e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-label">Expand collection sections by default</span>
                </label>

                {window.location.hostname !== "kisekidle.app" && (
                  <label className="premium-toggle">
                    <input
                      type="checkbox"
                      checked={showMigrationBanner}
                      onChange={(e) => setShowMigrationBanner(e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                    <span className="toggle-label">Show Migration Popup (kisekidle.app)</span>
                  </label>
                )}
              </div>

              <p style={{ fontSize: "0.7rem", opacity: 0.6, marginTop: "25px", borderTop: "1px solid var(--era-border)", paddingTop: "12px" }}>
                Logos by u/hyperdefiance
              </p>
            </motion.div>
          </Dialog.Content>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default SettingsModal;
